import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { queryClient } from './queryClient';

/**
 * Persists the whole React Query cache to sessionStorage so it survives a full
 * page refresh (F5) without leaving sensitive data at rest on disk.
 *
 * Why sessionStorage (not localStorage): sessionStorage survives reloads of the
 * same tab but is wiped when the tab/browser closes, and it isn't shared across
 * tabs. So the cache is fast on refresh yet doesn't persist the app's data
 * (financials/HR/permissions) to disk between browser sessions.
 *
 * Also cleared on explicit login AND logout (see AuthContext), so a different
 * user never sees the previous user's cached data. A normal refresh restores
 * the SAME user's session (cookie) and rehydrates their cache.
 */
const STORAGE_KEY = 'ERP_MAF_QUERY_CACHE';

// One-time cleanup: earlier builds persisted to localStorage. Remove any legacy
// on-disk copy so it doesn't outlive this migration.
try {
  window.localStorage.removeItem(STORAGE_KEY);
} catch {
  /* noop */
}

export const queryPersister = createSyncStoragePersister({
  storage: window.sessionStorage,
  key: STORAGE_KEY,
});

// Bump this string on a breaking change to cached shapes (or app schema) to
// force every client to discard its persisted cache after a deploy.
export const PERSIST_BUSTER = 'v1';

// How long a persisted cache may live before it's considered too old to restore.
export const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24; // 24h

/**
 * Wipe both the in-memory cache and the persisted copy. Call on login/logout so
 * no user's data lingers in the browser for the next user.
 */
export function clearPersistedQueries() {
  try {
    queryClient.clear();
  } catch {
    /* noop */
  }
  try {
    queryPersister.removeClient();
  } catch {
    /* noop */
  }
}
